import React from 'react';
import { format } from 'date-fns';
import type { ScheduleProjectSummary } from '../../../../types/workflows';
import type { MilestoneRow } from '../../../../AppSettings/services/MilestoneService';

type ScheduleTableViewProps = {
  projects: ScheduleProjectSummary[];
  totalFilteredCount: number;
  page: number;
  limit: number;
  navigatingToProject: string | null;
  onRowClick: (project: ScheduleProjectSummary) => void;
  onPageChange: React.Dispatch<React.SetStateAction<number>>;
  milestonesByProject: Record<string, MilestoneRow[]>;
};

const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) return '—';
  try {
    return format(new Date(dateString), 'MMM dd, yyyy');
  } catch {
    return dateString;
  }
};

const getMilestoneStatusClass = (milestone: MilestoneRow): string => {
  const status = (milestone.status || '').toLowerCase();
  if (milestone.isOverdue || status.includes('overdue')) return 'milestone-status-overdue';
  if (status.includes('complete') || status.includes('done')) return 'milestone-status-completed';
  if (status.includes('progress') || status.includes('active')) return 'milestone-status-inprogress';
  if (status.includes('block')) return 'milestone-status-blocked';
  if (status.includes('plan') || status.includes('upcoming')) return 'milestone-status-planned';
  return 'milestone-status-default';
};

const ScheduleTableView: React.FC<ScheduleTableViewProps> = ({
  projects,
  totalFilteredCount,
  page,
  limit,
  navigatingToProject,
  onRowClick,
  onPageChange,
  milestonesByProject,
}) => {
  const totalPages = Math.max(1, Math.ceil(totalFilteredCount / limit));

  return (
    <div className="schedule-table-container">
      <div className="table-header">
        <h2>Projects ({totalFilteredCount})</h2>
        <div className="table-info">
          <span>
            Showing {(page - 1) * limit + 1} - {Math.min(page * limit, totalFilteredCount)} of {totalFilteredCount}
          </span>
        </div>
      </div>
      
      <div className="schedule-table-scroll">
        <table className="schedule-table">
          <thead>
            <tr>
              <th className="sticky-col sticky-col-header">Project Name</th>
              <th>Client</th>
              <th>Status</th>
              <th>Owner</th>
              <th>Completed</th>
              <th>Overdue</th>
              <th>Milestones</th>
              <th>Next Planned Date</th>
              <th>Disciplines</th>
            </tr>
          </thead>
          <tbody>
            {projects.map((project) => (
              <tr 
                key={project.projectId}
                className={navigatingToProject === project.projectId ? 'navigating' : ''}
                onClick={() => onRowClick(project)}
                style={{ cursor: 'pointer' }}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onRowClick(project);
                  }
                }}
                aria-label={`View details for ${project.projectName}`}
              >
                {(() => {
                  const milestones = milestonesByProject[project.projectId] || [];
                  return (
                    <>
                      <td className="project-name-cell sticky-col">
                        <strong>{project.projectName}</strong>
                        {project.projectType && (
                          <span className="project-type-badge">{project.projectType}</span>
                        )}
                      </td>
                      <td>{project.clientName || '—'}</td>
                      <td>
                        <span className={`status-badge status-${project.projectStatus?.toLowerCase() || 'unknown'}`}>
                          {project.projectStatus || '—'}
                        </span>
                      </td>
                      <td>{project.ownerName || '—'}</td>
                      <td className="completed-count">{project.completedCount}</td>
                      <td className={project.overdueCount > 0 ? 'overdue-count warning' : 'overdue-count'}>
                        {project.overdueCount}
                      </td>
                      <td className="milestones-cell">
                        {milestones.length === 0 ? (
                          '—'
                        ) : (
                          <div className="milestone-list">
                            {milestones.map((milestone) => (
                              <div key={milestone.milestoneId} className="milestone-chip">
                                <div className="milestone-name">{milestone.name}</div>
                                <div className="milestone-meta">
                                  <span className={`milestone-status-badge ${getMilestoneStatusClass(milestone)}`}>
                                    {milestone.statusBadge?.label || milestone.status}
                                  </span>
                                  <span className="milestone-date">
                                    {formatDate(milestone.actualDate || milestone.plannedDate)}
                                  </span>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </td>
                      <td>{formatDate(project.nextPlannedDate)}</td>
                      <td>
                        {project.disciplines && project.disciplines.length > 0 ? (
                          <div className="disciplines-list">
                            {project.disciplines.slice(0, 2).map((d, idx) => (
                              <span key={idx} className="discipline-tag">{d}</span>
                            ))}
                            {project.disciplines.length > 2 && (
                              <span className="discipline-more">+{project.disciplines.length - 2}</span>
                            )}
                          </div>
                        ) : (
                          '—'
                        )}
                      </td>
                    </>
                  );
                })()}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalFilteredCount > limit && (
        <div className="pagination-controls">
          <button
            className="btn btn-secondary"
            onClick={() => onPageChange(p => Math.max(1, p - 1))}
            disabled={page === 1}
          >
            Previous
          </button>
          <span className="pagination-info">
            Page {page} of {totalPages}
          </span>
          <button
            className="btn btn-secondary"
            onClick={() => onPageChange(p => p + 1)}
            disabled={page >= totalPages}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default ScheduleTableView;

